import { useState } from 'react';

import { getMessageType } from '@/utils/messages';

import { DEFAULT_MESSAGE_TYPE } from '../Commits/constants';
import {
  GetMessageDiffsArgs,
  GetModelConfigDiffsArgs,
  GetToolDiffsArgs,
  StoredMessageDiffs,
  StoredMessageDiffsRecord,
  StoredModelConfigDiffs,
  StoredModelConfigDiffsRecord,
  StoredToolDiffs,
  StoredToolDiffsRecord,
} from '../Commits/types';
import {
  diffMessageTypes,
  diffMessages,
  diffModelConfig,
  diffSchema,
  diffTools,
} from '../utils';

export const useStoredDiffs = () => {
  const [storedMessageDiffs, setStoredMessageDiffs] =
    useState<StoredMessageDiffs>({});
  const [storedToolDiffs, setStoredToolDiffs] = useState<StoredToolDiffs>({});
  const [storedModelConfigDiffs, setStoredModelConfigDiffs] =
    useState<StoredModelConfigDiffs>({});

  const getMessageDiffs = ({
    commitId,
    previousCommitMsgs,
    currentCommitMsgs,
    previousCommitSchema,
    currentCommitSchema,
  }: GetMessageDiffsArgs): StoredMessageDiffsRecord => {
    const storedMsgDiff = storedMessageDiffs[commitId];

    if (storedMsgDiff) {
      return storedMsgDiff;
    }

    const messageDiffs = diffMessages(previousCommitMsgs, currentCommitMsgs);
    const schemaDiffs = diffSchema(previousCommitSchema, currentCommitSchema);
    const messageTypeDiffs = diffMessageTypes(
      previousCommitMsgs.map(
        (message) => getMessageType(message) ?? DEFAULT_MESSAGE_TYPE
      ),
      currentCommitMsgs.map(
        (message) => getMessageType(message) ?? DEFAULT_MESSAGE_TYPE
      )
    );

    setStoredMessageDiffs((prev) => ({
      ...prev,
      [commitId]: {
        messageDiffs,
        schemaDiffs,
        messageTypeDiffs,
      },
    }));

    return { messageDiffs, schemaDiffs, messageTypeDiffs };
  };

  const getToolDiffs = ({
    commitId,
    previousCommitTools,
    currentCommitTools,
  }: GetToolDiffsArgs): StoredToolDiffsRecord => {
    const storedToolDiff = storedToolDiffs[commitId];

    if (storedToolDiff) {
      return storedToolDiff;
    }

    const toolDiffs = diffTools(previousCommitTools, currentCommitTools);

    setStoredToolDiffs((prev) => ({
      ...prev,
      [commitId]: {
        toolDiffs,
      },
    }));

    return { toolDiffs };
  };

  const getModelConfigDiffs = ({
    commitId,
    previousCommitModelConfig,
    currentCommitModelConfig,
  }: GetModelConfigDiffsArgs): StoredModelConfigDiffsRecord => {
    const storedModelConfigDiff = storedModelConfigDiffs[commitId];

    if (storedModelConfigDiff) {
      return storedModelConfigDiff;
    }

    const modelConfigDiffs = diffModelConfig(
      previousCommitModelConfig,
      currentCommitModelConfig
    );

    setStoredModelConfigDiffs((prev) => ({
      ...prev,
      [commitId]: {
        modelConfigDiffs,
      },
    }));

    return { modelConfigDiffs };
  };

  return { getMessageDiffs, getToolDiffs, getModelConfigDiffs };
};
